import { memo, useCallback } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import toast from "react-hot-toast";
import * as Yup from "yup";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useApproveIssuanceRenewalIdForStaffFromContractedCompanies,
  useApproveLostStickerIDCardRequest,
  useApproveOfficeAccessAuthorization,
  useApproveParkingEntrySticker,
  useApprovePcInOutPermit,
  useApprovePermitToWork,
  useApproveWarehouseInOutPermit,
  useGetIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByID,
  useGetLostStickerIDCardRequestPermitDetailsByID,
  useGetOfficeAccessAuthorizationPermitDetailsByID,
  useGetParkingStickerRequestPermitDetailsByID,
  useGetPcInOutPermitDetailsByID,
  useGetPermitToWorkDetailsByID,
  useGetWarehouseInOutPermitDetailsByID,
  useSendFinalPermitEmail,
} from "../../../hooks/permit/mutation";
import { PERMIT_TYPES, PermitTypes } from "../../../constants/common";
import { generatePermitPDFAsFile } from "../../../utils/generatePermitPDFAsFile";
import { APP_ROUTES } from "../../../routes";
import { fileToBase64 } from "../../../lib/functions";

interface ApprovePermitModalProps {
  type: PERMIT_TYPES;
  isOpen: boolean;
  onOpenChange: () => void;
  permitId?: number;
}

const ApprovePermitModalSchema = Yup.object({
  reason: Yup.string(),
});

interface ApprovePermitModalData {
  reason?: string;
}

const ApprovePermitModal = ({
  type,
  permitId,
  isOpen,
  onOpenChange,
}: ApprovePermitModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useFormWithSchema(ApprovePermitModalSchema);

  const { t } = useTranslation();

  // Approval APIs
  const { mutateAsync: ApprovePcInOutPermit, isPending: isApprovePcInOutPermitLoading } = useApprovePcInOutPermit();
  const { mutateAsync: ApproveWarehouseInOutPermit, isPending: isApproveWarehouseInOutPermitLoading } = useApproveWarehouseInOutPermit();
  const { mutateAsync: ApprovePermitToWork, isPending: isApprovePermitToWorkLoading } = useApprovePermitToWork();
  const { mutateAsync: ApproveLostStickerIDCardRequest, isPending: isApproveLostStickerIDCardRequestLoading } = useApproveLostStickerIDCardRequest();
  const { mutateAsync: ApproveParkingEntrySticker, isPending: isApproveParkingEntryStickerLoading } = useApproveParkingEntrySticker();
  const { mutateAsync: ApproveOfficeAccessAuthorization, isPending: isApproveOfficeAccessAuthorizationLoading } = useApproveOfficeAccessAuthorization();
  const { mutateAsync: ApproveIssuanceRenewalIdForStaffFromContractedCompanies, isPending: isApproveIssuanceRenewalIdForStaffFromContractedCompaniesLoading } = useApproveIssuanceRenewalIdForStaffFromContractedCompanies();

  // GetPermitDetailsByID APIs
  const { mutateAsync: GetPcInOutPermitDetailsByID } = useGetPcInOutPermitDetailsByID();
  const { mutateAsync: GetWarehouseInOutPermitDetailsByID } = useGetWarehouseInOutPermitDetailsByID();
  const { mutateAsync: GetPermitToWorkDetailsByID } = useGetPermitToWorkDetailsByID();
  const { mutateAsync: GetLostStickerIDCardRequestPermitDetailsByID } = useGetLostStickerIDCardRequestPermitDetailsByID()
  const { mutateAsync: GetParkingStickerRequestPermitDetailsByID } = useGetParkingStickerRequestPermitDetailsByID()
  const { mutateAsync: GetOfficeAccessAuthorizationPermitDetailsByID } = useGetOfficeAccessAuthorizationPermitDetailsByID();
  const { mutateAsync: GetIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByID } = useGetIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByID()

  // SendFinalPermitEmail API
  const { mutateAsync: SendFinalPermitEmail } = useSendFinalPermitEmail();

  const navigate = useNavigate();

  const handleApprovePermit = useCallback(
    async (formData: ApprovePermitModalData) => {
      if (!permitId) return;

      try {
        let res;
        if (type === PERMIT_TYPES.PC_IN_OUT_PERMIT) {
          const body = {
            n_LaptopPermitID: permitId,
            s_Comments: formData.reason || null,
          };

          res = await ApprovePcInOutPermit(body);
        } else if (type === PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT) {
          const body = {
            n_PermitWarehouseID: permitId,
            s_Comments: formData.reason || null,
          };

          res = await ApproveWarehouseInOutPermit(body);
        } else if (type === PERMIT_TYPES.PERMIT_TO_WORK) {
          const body = {
            n_PermitToWorkID: permitId,
            s_Comments: formData.reason || null,
          };
          res = await ApprovePermitToWork(body);
        } else if (type === PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD) {
          const body = {
            lostStickerRequestId: permitId,
            comments: formData.reason || null,
          };
          res = await ApproveLostStickerIDCardRequest(body);
        } else if (type === PERMIT_TYPES.PARKING_STICKER_REQUEST) {
          const body = {
            parkingStickerRequestId: permitId,
            comments: formData.reason || null,
          };
          res = await ApproveParkingEntrySticker(body);
        } else if (type === PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION) {
          const body = {
            officeAccessAuthorisationRequestId: permitId,
            comments: formData.reason || null,
          };
          res = await ApproveOfficeAccessAuthorization(body);
        } else if (type === PERMIT_TYPES.ISSUANCE_RENEWAL_ID) {
          const body = {
            contractorStaffIdRequestId: permitId,
            comments: formData.reason || null,
          };
          res = await ApproveIssuanceRenewalIdForStaffFromContractedCompanies(body);
        }

        toast.success(t("permitApproved"));

        setTimeout(() => {
          navigate(APP_ROUTES.APPROVE_PERMIT_REQUEST);
        }, 700);

        // Then fetch the permit details to generate the PDF
        if (res?.isApproved) {
          const permitDetailsBody = {
            encryptedId: permitId.toString() || "",
          };

          let permitDetails;
          if (type === PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION) {
            permitDetails = await GetOfficeAccessAuthorizationPermitDetailsByID(permitDetailsBody);
          } else if (type === PERMIT_TYPES.ISSUANCE_RENEWAL_ID) {
            permitDetails = await GetIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByID(permitDetailsBody);
          } else if (type === PERMIT_TYPES.PARKING_STICKER_REQUEST) {
            permitDetails = await GetParkingStickerRequestPermitDetailsByID(permitDetailsBody);
          } else if (type === PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD) {
            permitDetails = await GetLostStickerIDCardRequestPermitDetailsByID(permitDetailsBody);
          } else if (type === PERMIT_TYPES.PC_IN_OUT_PERMIT) {
            permitDetails = await GetPcInOutPermitDetailsByID(permitDetailsBody);
          } else if (type === PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT) {
            permitDetails = await GetWarehouseInOutPermitDetailsByID(permitDetailsBody);
          } else if (type === PERMIT_TYPES.PERMIT_TO_WORK) {
            permitDetails = await GetPermitToWorkDetailsByID(permitDetailsBody);
          }

          if (!permitDetails) return;

          // Generate PDF File after successful permit approval
          const permitPDFFile = await generatePermitPDFAsFile(
            type,
            permitDetails,
            `permit_${type}_${Date.now()}.pdf`,
            t,
          );

          if (permitPDFFile) {
            // Download the PDF file so you can see it in the UI
            // const downloadUrl = URL.createObjectURL(permitPDFFile);
            // const downloadLink = document.createElement("a");
            // downloadLink.href = downloadUrl;
            // downloadLink.download = permitPDFFile.name;
            // document.body.appendChild(downloadLink);
            // downloadLink.click();
            // document.body.removeChild(downloadLink);
            // URL.revokeObjectURL(downloadUrl);

            try {
              const permitFormData = new FormData();
              permitFormData.append(
                "permitId",
                permitId.toString() || "",
              );
              permitFormData.append(
                "permitType",
                type === PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION ? PermitTypes.OFFICE_ACCESS_AUTHORIZATION.toString() :
                  type === PERMIT_TYPES.ISSUANCE_RENEWAL_ID ? PermitTypes.ISSUANCE_RENEWAL_ID.toString() :
                    type === PERMIT_TYPES.PARKING_STICKER_REQUEST ? PermitTypes.PARKING_STICKER_REQUEST.toString() :
                      type === PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD ? PermitTypes.LOST_VEHICLE_STICKER_ID_CARD.toString() :
                        type === PERMIT_TYPES.PC_IN_OUT_PERMIT ? PermitTypes.PC_IN_OUT_PERMIT.toString() :
                          type === PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT ? PermitTypes.WAREHOUSE_IN_OUT_PERMIT.toString() :
                            type === PERMIT_TYPES.PERMIT_TO_WORK ? PermitTypes.PERMIT_TO_WORK.toString() : ""
              );
              permitFormData.append("File", permitPDFFile);

              await SendFinalPermitEmail(permitFormData);
            } catch (error: any) { }
          } else {
            console.error("Failed to generate permit PDF file");
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        onOpenChange();
      }
    },
    [
      permitId,
      type,
      t,
      ApprovePcInOutPermit,
      ApproveWarehouseInOutPermit,
      ApprovePermitToWork,
      ApproveLostStickerIDCardRequest,
      ApproveParkingEntrySticker,
      ApproveOfficeAccessAuthorization,
      ApproveIssuanceRenewalIdForStaffFromContractedCompanies,
      navigate,
      onOpenChange,
      GetOfficeAccessAuthorizationPermitDetailsByID,
      GetIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByID,
      GetParkingStickerRequestPermitDetailsByID,
      GetLostStickerIDCardRequestPermitDetailsByID,
      GetPcInOutPermitDetailsByID,
      GetWarehouseInOutPermitDetailsByID,
      GetPermitToWorkDetailsByID,
    ],
  );

  const onClose = useCallback(() => {
    reset({ reason: "" });
    onOpenChange();
  }, [onOpenChange, reset]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="lg:max-w-[90vw]"
    >
      <div className="max-h-[70vh] overflow-y-auto w-full pb-1">
        <form
          onSubmit={handleSubmit(handleApprovePermit)}
          className="flex flex-col gap-5 justify-center items-center w-full"
        >
          <p className="text-lg font-medium">{t("confirmApproval")}</p>

          <div className="flex flex-col gap-3 w-3/4">
            <Controller
              name="reason"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("commentsOptional")}
                  placeholder={t("leaveCommentOptional")}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  value={value}
                />
              )}
            />
          </div>

          <div className="flex gap-2 items-center">
            <Button
              disabled={
                isApprovePcInOutPermitLoading ||
                isApproveWarehouseInOutPermitLoading ||
                isApprovePermitToWorkLoading || isApproveLostStickerIDCardRequestLoading
                || isApproveParkingEntryStickerLoading || isApproveOfficeAccessAuthorizationLoading
                || isApproveIssuanceRenewalIdForStaffFromContractedCompaniesLoading
              }
              isLoading={
                isApprovePcInOutPermitLoading ||
                isApproveWarehouseInOutPermitLoading ||
                isApprovePermitToWorkLoading || isApproveLostStickerIDCardRequestLoading
                || isApproveParkingEntryStickerLoading || isApproveOfficeAccessAuthorizationLoading
                || isApproveIssuanceRenewalIdForStaffFromContractedCompaniesLoading
              }
            >
              {t("approve")}
            </Button>

            <Button
              onClick={onClose}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("close")}
            </Button>
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default memo(ApprovePermitModal);
